import useSwr from 'swr'
import { API_URL } from '@/utils/constants'
import { strictFetcher } from './strict-fetcher'

/// One decoded alkane action from `GET /alkane/address/:addr/activity`.
///
/// The server decodes the address's OWN transactions (it owns an input) back
/// through the indexer's protostone reader, so every field here is read off
/// chain rather than reported by a tracker. Fields it cannot know are `null`,
/// never a stand-in: an `amount_sats` of null means the federation signer was
/// unresolvable, not that the wrap was free.
export interface ActivityEvent {
	txid: string
	height: number | null
	ts: number | null
	/// - `swap`          — an AMM route; `path` is the alkane ids it walked.
	/// - `wrap`          — BTC → frBTC through the signer.
	/// - `wrap_swap_leg` — a wrap whose swap leg is also inside this window.
	/// - `unwrap`        — frBTC burned; the BTC settles in a later signer tx.
	/// - `transfer`      — alkanes moved, in a shape we do not decode further.
	kind: 'swap' | 'wrap' | 'wrap_swap_leg' | 'unwrap' | 'transfer'
	path: string[] | null
	/// Raw base units that entered the action (frBTC/alkane, 8dp).
	amount_in: string | null
	/// The route's `min_out` FLOOR — not the fill. The chain records what the
	/// trade refused to go below, never what it actually received.
	min_out: string | null
	/// Sats paid to the signer on a wrap.
	amount_sats: number | null
}

interface ActivityResponse {
	address: string
	events: ActivityEvent[]
}

/// How many decoded actions to ask for. The endpoint's own ceiling is 50, and
/// 50 costs it two esplora pages; it is also the window every cost-basis
/// figure in the portfolio is scoped to, so it is stated in the UI.
export const ACTIVITY_LIMIT = 50

/// The address's on-chain alkane actions. One hook, called ONCE per tree and
/// passed down: Activity renders it and the portfolio derives cost basis from
/// the same array, and two subscriptions would mean the two surfaces could
/// disagree about the same wallet.
export default function useActivity(
	address: string | null,
	limit: number = ACTIVITY_LIMIT
): {
	events: ActivityEvent[]
	isLoading: boolean
	reachable: boolean
	unavailable: boolean
} {
	const { data, isLoading, error } = useSwr<ActivityResponse>(
		address ? `${API_URL}/alkane/address/${address}/activity?limit=${limit}` : null,
		strictFetcher,
		{ revalidateOnFocus: false, refreshInterval: 120_000 }
	)
	return {
		events: Array.isArray(data?.events) ? data!.events : [],
		isLoading,
		reachable: data !== undefined,
		unavailable: error !== undefined
	}
}
